// plantDataService.js
import axios from 'axios';

// Constants for localStorage
const PLANT_DATA_KEY = 'gardenGuru_plantData';
const PLANT_DATA_TIMESTAMP_KEY = 'gardenGuru_plantDataTimestamp';
const PLANT_DATA_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';
// const API_BASE_URL = 'http://localhost:5000/api';

// Subscribers for plant data changes
let subscribers = [];

const plantDataService = {
  // Get plant data from localStorage or API
  getPlantData: async () => {
    try {
      // Check if we have valid cached data
      if (plantDataService.isCachedDataValid()) {
        const cachedData = JSON.parse(localStorage.getItem(PLANT_DATA_KEY));
        console.log('Loading plant data from localStorage');
        
        if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
          return cachedData;
        }
      }
      
      // If no valid cached data, fetch from API
      console.log('No valid cached data, fetching from API');
      return await plantDataService.fetchPlantData();
    } catch (error) {
      console.error('Error loading plant data:', error);
      throw error;
    }
  },
  
  // Fetch plant data from API
  fetchPlantData: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/database/tables/plant/data`);
      
      if (response.data.status === 'success' && response.data.sampleData) {
        const plants = response.data.sampleData;
        
        // Save to localStorage with timestamp
        try {
          localStorage.setItem(PLANT_DATA_KEY, JSON.stringify(plants));
          localStorage.setItem(PLANT_DATA_TIMESTAMP_KEY, new Date().getTime().toString());
          console.log('Plant data saved to localStorage');
          
          // Notify subscribers
          notifySubscribers(plants);
        } catch (storageError) {
          // Handle localStorage errors (e.g., quota exceeded)
          console.error('Failed to save to localStorage:', storageError);
        }
        
        return plants;
      } else {
        throw new Error('Failed to fetch plant data');
      }
    } catch (error) {
      console.error('Error fetching plant data:', error);
      throw error;
    }
  },
  
  // Check if cached data is valid
  isCachedDataValid: () => {
    try {
      const timestamp = localStorage.getItem(PLANT_DATA_TIMESTAMP_KEY);
      if (!timestamp) return false;
      
      // Check if the data is still fresh (less than PLANT_DATA_EXPIRY old)
      const now = new Date().getTime();
      return (now - parseInt(timestamp)) < PLANT_DATA_EXPIRY;
    } catch (error) {
      console.error('Error checking cached data validity:', error);
      return false;
    }
  },
  
  // Clear plant data cache
  clearPlantDataCache: () => {
    try {
      localStorage.removeItem(PLANT_DATA_KEY);
      localStorage.removeItem(PLANT_DATA_TIMESTAMP_KEY);
      console.log('Plant data cache cleared');
      return true;
    } catch (error) {
      console.error('Error clearing plant data cache:', error);
      return false;
    }
  },
  
  // Subscribe to plant data changes
  subscribe: (callback) => {
    subscribers.push(callback);
    
    // Return unsubscribe function
    return () => {
      subscribers = subscribers.filter(cb => cb !== callback);
    };
  }
};

// Helper function to notify subscribers of data changes
function notifySubscribers(data) {
  subscribers.forEach(callback => callback(data));
}

export default plantDataService;